import React from 'react'
import { Box, Avatar, Tooltip, Fab, Badge } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import MenuIcon from '@mui/icons-material/Menu'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

/**
 * CollapsedDrawerStrip - Floating button to expand the waiting/support drawer
 * 
 * Shows when the drawer is collapsed, providing an easy way to re-open it.
 * Features:
 * - Hamburger menu icon for intuitive UX
 * - Mentor avatar overlay
 * - Pulsing animation to draw attention
 * - Mobile-friendly touch targets
 */
const CollapsedDrawerStrip = ({ 
  onClick, 
  mentorName, 
  mentorProfilePicId,
  showSupport = true 
}) => {
  // getImageLink expects a string (media ID), not an object
  const mentorImageUrl = mentorProfilePicId 
    ? getImageLink(mentorProfilePicId)
    : null

  return (
    <Tooltip 
      title="Tap to open session info & support" 
      placement="left" 
      arrow
      enterDelay={500}
    >
      <Fab
        onClick={onClick}
        color="primary"
        aria-label="Open session info"
        sx={{
          position: 'fixed',
          right: { xs: '16px', md: '24px' },
          top: '50%', // Centered vertically
          transform: 'translateY(-50%)',
          width: { xs: '60px', md: '64px' },
          height: { xs: '60px', md: '64px' },
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          boxShadow: '0 8px 32px rgba(139, 92, 246, 0.5), 0 4px 16px rgba(0, 0, 0, 0.2)',
          border: '3px solid rgba(255, 255, 255, 0.3)',
          cursor: 'pointer',
          zIndex: 1400, // Above video components
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          // Pulsing glow animation to draw attention
          animation: 'glowPulse 2s ease-in-out infinite',
          '@keyframes glowPulse': {
            '0%, 100%': {
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.5), 0 4px 16px rgba(0, 0, 0, 0.2)',
            },
            '50%': {
              boxShadow: '0 8px 40px rgba(139, 92, 246, 0.7), 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 20px rgba(139, 92, 246, 0.4)',
            },
          },
          '&:hover': {
            transform: 'translateY(-50%) scale(1.1)',
            boxShadow: '0 12px 40px rgba(139, 92, 246, 0.6), 0 6px 20px rgba(0, 0, 0, 0.3)',
            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
          },
          '&:active': {
            transform: 'translateY(-50%) scale(0.95)',
          },
        }}
      >
        <Badge
          badgeContent={
            <SupportAgentIcon sx={{ fontSize: 12, color: 'white' }} />
          }
          sx={{
            '& .MuiBadge-badge': {
              background: '#10b981',
              border: '2px solid white',
              minWidth: '20px',
              height: '20px',
              borderRadius: '10px',
              top: '-4px',
              right: '-4px',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {/* Hamburger Menu Icon */}
            <MenuIcon 
              sx={{ 
                color: 'white', 
                fontSize: { xs: 28, md: 32 },
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
              }} 
            />
          </Box>
        </Badge>
      </Fab>
    </Tooltip>
  )
}

export default CollapsedDrawerStrip
